import { ALL_GRANTS, PERMISSION_CATALOG, SWITCH_KEYS, type PermissionArea, type SwitchKey } from '../constants/permissions.js'
import type { Grants } from '../dto/tenant-role.dto.js'
import type { ApiTokenScope } from '../enums/api-token-scope.enum.js'
import { isStaffRole, type Role } from '../enums/role.enum.js'
import type { TenantRoleReach } from '../enums/tenant-role-reach.enum.js'

/** Something a role may be given: an area at View or Edit, or a switch. */
export type PermissionNeed = `${PermissionArea}:view` | `${PermissionArea}:edit` | SwitchKey

function isSwitch(need: PermissionNeed): need is SwitchKey {
  return SWITCH_KEYS.some((key) => key === need)
}

/**
 * Whether grants meet a need (docs/custom-roles.spec.md, "Permissions"). Edit
 * includes View; a switch stands alone. Shared by the API, which decides, and
 * the web app, which shows only what will be allowed. An area the catalog
 * doesn't have is refused, never read as some other area.
 */
export function grantsMeet(grants: Grants, need: PermissionNeed): boolean {
  if (isSwitch(need)) return grants[need]
  const at = need.lastIndexOf(':')
  const area = PERMISSION_CATALOG.find((a) => a.key === need.slice(0, at))?.key
  if (area === undefined) return false
  const have = grants[area]
  return have === 'edit' || (need.slice(at + 1) === 'view' && have === 'view')
}

const LEVEL_RANK = { none: 0, view: 1, edit: 2 } as const

/**
 * Whether every permission in `given` is at or below `holder`'s: area by area,
 * and each switch only if the holder has it. The People Edit guardrail
 * (docs/custom-roles.spec.md, "People and the guardrail"): nobody gives a role
 * that can do something they can't.
 */
export function grantsAtMost(given: Grants, holder: Grants): boolean {
  if (SWITCH_KEYS.some((key) => given[key] && !holder[key])) return false
  return PERMISSION_CATALOG.every((area) => LEVEL_RANK[given[area.key]] <= LEVEL_RANK[holder[area.key]])
}

/** Whether a role at `given` reaches no wider than `holder`: assigned venues is within either, company-wide only within company-wide. */
export function reachAtMost(given: TenantRoleReach, holder: TenantRoleReach): boolean {
  return given === 'venues' || holder === 'company'
}

/**
 * The areas each token scope reaches (docs/api-token-scopes.md). A scope is worth
 * offering when the role can use any of them: what a token can do is its scope
 * and its owner's permissions both, so a scope beyond the role would only be
 * refused route by route. `UPLOADS` (a cover, an avatar) serves any staff.
 */
const SCOPE_AREAS: Readonly<Record<Exclude<ApiTokenScope, 'VENDOR_SELF' | 'UPLOADS'>, readonly PermissionArea[]>> = {
  EVENTS: ['events'],
  VENUES: ['spaces', 'venueSetup', 'marketing', 'venues', 'furniture'],
  PEOPLE: ['people', 'clients'],
  IMPORTS: ['imports'],
  FURNITURE: ['furniture'],
  VENDOR_APPROVALS: ['vendors'],
  ADS: ['ads'],
  LEAD_STATS: ['leadStats'],
}

/** A vendor's own scopes; `EVENTS` reaches the one events route a vendor has, an event's furniture. */
const VENDOR_SCOPES: readonly ApiTokenScope[] = ['VENDOR_SELF', 'EVENTS', 'UPLOADS']

/**
 * The scopes someone may put on a token or a connection: for staff, those their
 * role's permissions can use; for a vendor, its own; none for a client or CSE,
 * whose long-lived credential would be a liability without a purpose, or reach
 * every company on the platform. `grants` is the staff member's role, every
 * grant for an organization admin.
 */
export function apiTokenScopesFor(holder: { role: Role; grants: Grants | null }): readonly ApiTokenScope[] {
  if (holder.role === 'VENDOR') return VENDOR_SCOPES
  if (!isStaffRole(holder.role)) return []
  const grants = holder.role === 'ORGANIZATION_ADMIN' ? ALL_GRANTS : holder.grants
  if (grants === null) return []
  return STAFF_SCOPES.filter((scope) => scope === 'UPLOADS' || SCOPE_AREAS[scope].some((area) => grants[area] !== 'none'))
}

/** Staff scopes in the order the token screen lists them. */
const STAFF_SCOPES: readonly Exclude<ApiTokenScope, 'VENDOR_SELF'>[] = [
  'EVENTS',
  'VENUES',
  'PEOPLE',
  'IMPORTS',
  'FURNITURE',
  'VENDOR_APPROVALS',
  'UPLOADS',
  'ADS',
  'LEAD_STATS',
]
